import type { IPrintReceiptCmd } from "@/data/commands/print/PrintPaymentReceiptCmd";
import type { IReprintOrderCmd } from "@/data/commands/print/ReprintOrderCmd";
import type { IPrintJob, IPrintOrderReceiptAvailable, IPrintPaymentReceiptAvailable } from "@/data/model/Print";
import type { IPrintJobsQry } from "@/data/queries/print/PrintJobsQry";
import type { IPrintOrderReceiptAvailableQuery } from "@/data/queries/print/PrintOrderReceiptAvailableQuery";
import type { IPrintPaymentReceiptAvailableQuery } from "@/data/queries/print/PrintPaymentReceiptAvailableQuery";
import { DateTime } from "@/data/util/DateTime";

import { BaseServiceDao, type IDataSource, type IPaginatedResult, parseCollection } from "./BaseServiceDao";
import type {IPrintInterimBill} from "@/data/commands/print/IPrintInterimBill";

export class PrintDao extends BaseServiceDao implements IDataSource<IPrintJob> {
    public static className = "PrintDao";

    constructor() {
        super("print");
        this.isLocationDependent = true;
    }

    public async printOrderReceipt(orderId: string, cmd: IPrintReceiptCmd): Promise<void> {
        return this.post("order-receipt/" + orderId, cmd);
    }

    public async printPaymentReceipt(paymentId: string, cmd: IPrintReceiptCmd): Promise<void> {
        return this.post("payment-receipt/" + paymentId, cmd);
    }

    public async printInterimBill(billId: string, cmd: IPrintInterimBill): Promise<void> {
        return this.post("interim-bill/" + billId, cmd);
    }

    public async reprintOrder(orderId: string, cmd: IReprintOrderCmd): Promise<void> {
        return this.post("reprintorder/" + orderId, cmd);
    }

    public async cancelJob(jobId: string): Promise<void> {
        return this.delete(jobId + "/cancel", {});
    }

    public async deleteJob(jobId: string): Promise<void> {
        return this.delete(jobId, {});
    }

    public async getData(qry: IPrintJobsQry | any): Promise<IPaginatedResult<IPrintJob>> {
        return this.get("", qry, (f) => {
            return {
                hasNext: false,
                hasPrev: false,
                items: parseCollection<IPrintJob>(f, PrintDao.parsePrintJob),
                limit: f.length,
                offset: 0,
            };
        });
    }

    public async getIsPrintOrderReceiptAvailable(
        qry: IPrintOrderReceiptAvailableQuery
    ): Promise<IPrintOrderReceiptAvailable> {
        return this.get("print-order-receipt-available", qry, PrintDao.parseIsPrintOrderReceiptAvailableResult);
    }

    public async getIsPrintPaymentReceiptAvailable(
        qry: IPrintPaymentReceiptAvailableQuery
    ): Promise<IPrintPaymentReceiptAvailable> {
        return this.get("print-payment-receipt-available", qry, PrintDao.parseIsPrintPaymentReceiptAvailableResult);
    }

    public static parsePrintJob(literal: any): IPrintJob {
        return {
            id: literal.id,
            created: DateTime.parseIsoDate(literal.created),
            escPosData: literal.escPosData,
            productionSiteId: literal.productionSiteId,
            name: literal.name,
            state: parseInt(literal.state),
            failReason: literal.failReason == null ? undefined : literal.failReason,
            isBackupPrint: literal.isBackupPrint,
            forwardedTo: literal.forwardedTo == null ? undefined : literal.forwardedTo,
            backupProductionSiteId: literal.backupProductionSiteId == null ? undefined : literal.backupProductionSiteId,
        };
    }

    public static parseIsPrintOrderReceiptAvailableResult(literal: any): IPrintOrderReceiptAvailable {
        return {
            orderId: literal.orderId,
            locationId: literal.locationId,
            isPrintReceiptAvailable: literal.isPrintReceiptAvailable,
        };
    }

    public static parseIsPrintPaymentReceiptAvailableResult(literal: any): IPrintPaymentReceiptAvailable {
        return {
            paymentId: literal.paymentId,
            locationId: literal.locationId,
            isPrintReceiptAvailable: literal.isPrintReceiptAvailable,
        };
    }
}
